<template>

  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title float-left pr-1 mb-0">
              Create new Retail Coupon
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="retailCouponCreateForm"
        #default="{ invalid }"
      >
        <b-card>
          <b-row>
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="Coupon Code"
                vid="couponCode"
                rules="required"
              >
                <b-form-group
                  label="Coupon Code*"
                  label-for="coupon-code"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-input
                    id="coupon-code"
                    v-model="couponCode"
                    placeholder="Enter a code, e.g. qm202620"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-muted">Must be unique for every coupon</small>
                  <br>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Coupon Title"
                vid="couponTitle"
                rules="required"
              >
                <b-form-group
                  label="Coupon Title*"
                  label-for="coupon-title"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-input
                    id="coupon-title"
                    v-model="couponTitle"
                    placeholder="Enter a name for the coupon"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-muted">This is displayed to users</small>
                  <br>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Quota per User"
                vid="quotaPerUser"
                rules="required|integer"
              >
                <b-form-group
                  label="Quota per User*"
                  label-for="quota-per-user"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-input
                    id="quota-per-user"
                    v-model="quotaPerUser"
                    type="number"
                    min="1"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Total Quota"
                vid="totalQuota"
                rules="required|integer"
              >
                <b-form-group
                  label="Total Quota*"
                  label-for="total-quota"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-input
                    id="total-quota"
                    v-model="totalQuota"
                    type="number"
                    min="1"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="{ errors }"
                name="Status"
                vid="status"
                rules="required"
              >
                <b-form-group
                  label="Status*"
                  label-for="status"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <v-select
                    id="status"
                    v-model="status"
                    label="title"
                    placeholder="Select from list"
                    :options="statusOptions"
                    :reduce="title => title.code"
                    :clearable="false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Usable From"
                label-for="usable-from"
                label-cols-md="5"
              >
                <b-input-group>
                  <flat-pickr
                    id="usable-from"
                    v-model="usableFrom"
                    class="form-control flat-pickr-group"
                    placeholder="Today"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      class="cursor-pointer"
                      data-toggle
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-muted">Leave blank if usable forever</small>
              </b-form-group>

              <b-form-group
                label="Usable Until"
                label-for="usable-until"
                label-cols-md="5"
              >
                <b-input-group>
                  <flat-pickr
                    id="usable-until"
                    v-model="usableUntil"
                    class="form-control flat-pickr-group"
                    placeholder="Choose a date"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      class="cursor-pointer"
                      data-toggle
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-muted">Must be unique for every coupon</small>
              </b-form-group>

              <validation-provider
                #default="{ errors }"
                name="Value"
                vid="value"
                rules="required"
              >
                <b-form-group
                  label="Value*"
                  label-for="value"
                  label-cols-md="5"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="value"
                      v-model="value"
                      type="number"
                      min="0"
                      :state="errors.length > 0 ? false : null"
                    />
                    <b-input-group-append>
                      <b-form-select
                        v-model="valueType"
                        :options="valueTypeOptions"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item ml-auto">
            <b-button
              type="button"
              variant="flat-primary"
              :to="{ name: 'products-retail-coupons-index' }"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              <span class="align-middle">Create Retail Coupon</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BFormSelect, BInputGroup, BInputGroupAppend, BNav,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
import { required, integer } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BNav,
    vSelect,
    flatPickr,

    ValidationProvider,
    ValidationObserver,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data() {
    return {
      couponCode: '',
      couponTitle: '',
      quotaPerUser: null,
      totalQuota: null,
      status: 'Active',
      usableFrom: '',
      usableUntil: '',
      value: null,
      valueType: 'Percentage',

      statusOptions: [
        { title: 'Active', code: 'Active' },
        { title: 'Inactive', code: 'Inactive' },
      ],
      valueTypeOptions: [
        { value: 'Percentage', text: '% Percentage' },
        { value: 'Dollar', text: 'S$ Amount' },
      ],

      flatPickrConfig: {
        wrap: true, enableTime: false, dateFormat: 'd/m/Y', disableMobile: true,
      },

      required,
      integer,
    }
  },
  methods: {
    submitForm() {
      this.$refs.retailCouponCreateForm.validate().then(success => {
        if (success) {
          const formData = new FormData()

          formData.append('couponCode', this.couponCode)
          formData.append('couponTitle', this.couponTitle)
          formData.append('quotaPerUser', this.quotaPerUser)
          formData.append('totalQuota', this.totalQuota)
          formData.append('status', this.status)
          formData.append('usableFrom', this.usableFrom || '')
          formData.append('usableUntil', this.usableUntil || '')
          formData.append('value', this.value)
          formData.append('valueType', this.valueType)

          this.$http.post('products/retail-coupons/store', formData)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Retail Coupon has been created successfully.',
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              this.$router.push({ name: 'products-retail-coupons-index' })
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },
  },
}
</script>
